import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react';

import { SafeHtml } from '../../../../../components';
import { Accordion, IconButton } from '../../../../../compositions';
import { ProductVariant } from '../../../../../entities/@products/ProductVariant/ProductVariant';
import { trimToMaxLength } from '../../../../../helpers/string';
import { useDeviceWidth, useTrans } from '../../../../../hooks';
import { ProductTableInformation } from '../ProductTableInformation/ProductTableInformation';

import './ProductInformation.scss';

interface ProductInformationProps {
    productVariant: ProductVariant;
    className?: string;
}

export const ProductInformation: FC<ProductInformationProps> = ({
    productVariant,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const { isMobile } = useDeviceWidth();

    const maxCharactersForDescription = 250;

    const [description, setDescription] = useState<string>('');
    const [descriptionShowMore, setDescriptionShowMore] = useState<boolean>(false);

    const descriptionIsTooLong = productVariant.description.length >= maxCharactersForDescription;

    useEffect(() => {
        if (descriptionIsTooLong && !descriptionShowMore) {
            const truncatedDescription = trimToMaxLength(productVariant.description, maxCharactersForDescription);
            setDescription(truncatedDescription);
        } else {
            setDescription(productVariant.description);
        }

        setDescriptionShowMore(false);
    }, [productVariant.description]);

    const handleShowMoreDescription = (): void => {
        if (descriptionShowMore) {
            const truncatedDescription = trimToMaxLength(productVariant.description, maxCharactersForDescription);

            setDescription(truncatedDescription);
        } else {
            setDescription(productVariant.description);
        }

        setDescriptionShowMore(!descriptionShowMore);
    };

    return (
        <section className={`product-information ${className}`}>
            <div className="product-information__main-inner">
                <div className="product-information__description-wrapper">
                    <h2 className="product-information__title">
                        {trans('containers.productDetailRenderer.description')}
                    </h2>

                    {productVariant.description ? (
                        <SafeHtml
                            html={description}
                            className="product-information__description"
                        />
                    ) : (
                        <p className="product-information__description">
                            {trans('containers.productDetailRenderer.noDescriptionAvailable')}
                        </p>
                    )}

                    {descriptionIsTooLong && (
                        <IconButton
                            isSmall
                            icon={descriptionShowMore ? 'chevron-up' : 'chevron-down'}
                            text={trans(`common.${descriptionShowMore ? 'showLess' : 'showMore'}`)}
                            onClick={handleShowMoreDescription}
                            className="product-information__show-more-description-button"
                        />
                    )}
                </div>

                <aside className="product-information__details-wrapper">
                    {productVariant.dimensionAttributes.length > 0 && (
                        <div className="product-information__dimensions-wrapper">
                            <Accordion
                                hasBorder={isMobile}
                                heading={trans('containers.productDetailRenderer.dimensions')}
                                className="product-information__title"
                                headingClassName="product-information__details-heading"
                            >
                                <ProductTableInformation attributes={productVariant.dimensionAttributes} />
                            </Accordion>
                        </div>
                    )}

                    {productVariant.attributes.length > 0 && (
                        <div className="product-information__specifications-wrapper">
                            <Accordion
                                hasBorder={isMobile}
                                heading={trans('containers.productDetailRenderer.specifications')}
                                className="product-information__title"
                                headingClassName="product-information__details-heading"
                            >
                                <ProductTableInformation attributes={productVariant.attributes} />
                            </Accordion>
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
};
